import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  ProdukttabelleEnum,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapVorbelegungdaten,
  RsVorbelegungdaten,
  VermietetesObjekt,
  Vorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import { RsVorvertragsdaten, SpartenType, VertragsdatenRequestModel } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { getBebauteObjekte, getUnbebauteObjekte } from '@ps20-frontend/reducers';
import { RsActions } from '../rsActions';
import { IAppState } from '../appState';

const rsActions = new RsActions();
const globalActions = new GlobalActions();

export enum VorbelegungRsRequestEventsEnum {
  VORBELEGUNG_RS_REQUEST_SUCCESSFULL = 'VORBELEGUNG_RS_REQUEST_SUCCESSFULL',
  VORBELEGUNG_RS_REQUEST_FAILED = 'VORBELEGUNG_RS_REQUEST_FAILED',
}

export function* watchVorbelegungRequested() {
  yield takeEvery(GlobalActions.VORBELEGUNG_REQUESTED, safeSaga(vorbelegungSaga, App.RS));
  yield takeEvery(
    VorbelegungRsRequestEventsEnum.VORBELEGUNG_RS_REQUEST_SUCCESSFULL,
    safeSaga(successCallback, App.RS),
  );
  yield takeEvery(
    VorbelegungRsRequestEventsEnum.VORBELEGUNG_RS_REQUEST_FAILED,
    safeSaga(errorCallback, App.RS),
  );
}

export function* vorbelegungSaga(action: GlobalAction<VorbelegungPayload>) {
  if (action.payload.appId !== App.RS) {
    return;
  }

  if (action.payload?.vorhandeneVorbelegungdaten) {
    yield* rsDatenVorbelegen(action.payload.vorhandeneVorbelegungdaten);
  } else {
    const globalPut = GlobalPut.getInstance();
    const vertragsdatenRequestModel: VertragsdatenRequestModel = {
      spLaufnummer: action.payload.spLaufnummer,
      vertragsnummer: action.payload.vertragsnummer,
    };
    yield call(
      [globalPut, globalPut.put],
      globalActions.doRequest({
        appId: App.RS,
        body: vertragsdatenRequestModel,
        successEvent: VorbelegungRsRequestEventsEnum.VORBELEGUNG_RS_REQUEST_SUCCESSFULL,
        errorEvent: VorbelegungRsRequestEventsEnum.VORBELEGUNG_RS_REQUEST_FAILED,
        url: 'privatschutz/api/vertragsdaten',
      }),
    );
  }
}

export function* successCallback(successAction: DoRequestResponse<RsVorvertragsdaten>) {
  const rsVorbelegungdaten: RsVorbelegungdaten = mapVorbelegungdaten(
    SpartenType.RECHTSSCHUTZ,
    successAction.payload,
  ) as RsVorbelegungdaten;
  yield* rsDatenVorbelegen(rsVorbelegungdaten);
}

export function* rsDatenVorbelegen(vorbelegungdaten: Vorbelegungdaten) {
  const state: IAppState = yield select();
  const rsVorbelegungdaten: RsVorbelegungdaten = vorbelegungdaten as RsVorbelegungdaten;
  const globalPut = GlobalPut.getInstance();
  yield put(rsActions.familienstandChanged(rsVorbelegungdaten.familienstand));
  yield put(rsActions.oeffentlicherDienstChanged(rsVorbelegungdaten.oeffentlicherDienst));
  yield put(rsActions.selbststaendigkeitChanged(rsVorbelegungdaten.selbststaendig));

  // Vorbeleung Vermietete Objekte
  const vermieteteObjekte: Array<VermietetesObjekt> = [];
  if (rsVorbelegungdaten.bebauteGebaeude?.length > 0) {
    getBebauteObjekte(rsVorbelegungdaten, vermieteteObjekte);
  }
  if (rsVorbelegungdaten.unbebauteGrundstuecke?.length > 0) {
    getUnbebauteObjekte(rsVorbelegungdaten, vermieteteObjekte);
  }
  if (vermieteteObjekte.length > 0) {
    yield put(rsActions.overwriteVermieteteObjekte(vermieteteObjekte));
  }
  if (vorbelegungdaten.internerVertragsbezugs) {
    const action = globalActions.internerVertragsbezugReceived(
      vorbelegungdaten.internerVertragsbezugs,
      App.RS,
      state.angebotsId,
    );
    yield put(action);
  }

  if (!state.produkt?.produktlinien) {
    yield take(GlobalActions.PRODUKT_RECEIVED);
  }

  if (rsVorbelegungdaten.kfzOnly === true) {
    yield call(
      [globalPut, globalPut.put],
      globalActions.produktRequested({
        appId: App.RS,
        produkttabelle: ProdukttabelleEnum.RS_VERKEHR,
      }),
    );
    yield take(GlobalActions.PRODUKT_RECEIVED);
  }

  if (rsVorbelegungdaten.vorvertragBausteinList) {
    const bausteinChanges = rsVorbelegungdaten.vorvertragBausteinList.map(baustein => {
      const change: { name: string; status: boolean } = {
        name: baustein.taasTarifrisikoschluessel,
        status: true,
      };
      return change;
    });
    if (bausteinChanges.length > 0) {
      yield put(rsActions.overwriteZusatzoptionen(bausteinChanges));
    }
  }

  if (!state.vertragsoptionen?.beibehalten) {
    yield call(
      [globalPut, globalPut.put],
      globalActions.navigationRequested({
        appId: App.RS,
        subroute: '/eingabe',
      }),
    );
  } else {
    yield call(
      [globalPut, globalPut.put],
      globalActions.vorbelegungForBeibehaltungRequested(rsVorbelegungdaten),
    );
  }
  yield call([globalPut, globalPut.put], globalActions.vorbelegungFinished({ appId: App.RS, vorhandeneVorbelegungdaten: rsVorbelegungdaten }));
}

export function* errorCallback() {
  const globalPut = GlobalPut.getInstance();
  yield call([globalPut, globalPut.put], globalActions.vorbelegungFinished({ appId: App.RS }));
  yield call(
    [globalPut, globalPut.put],
    globalActions.navigationRequested({
      appId: App.RS,
      subroute: '/eingabe',
    }),
  );
}
